import { api } from '@/shared/lib/api'
import type { Client, ClientFormData, ClientFilters, ClientStatus } from '../types'

interface ClientPaginatedResponse {
  data: Client[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

function buildFilterParams(filters: ClientFilters): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: filters.page,
    limit: filters.limit,
  }
  if (filters.search) params.search = filters.search
  if (filters.commune) params.commune = filters.commune
  if (filters.gender) params.gender = filters.gender
  if (filters.last_known_category) params.last_known_category = filters.last_known_category
  if (filters.id_branch_office !== undefined) params.id_branch_office = filters.id_branch_office
  if (filters.created_from) params.created_from = filters.created_from
  if (filters.created_to) params.created_to = filters.created_to
  if (filters.sort_by) params.sort_by = filters.sort_by
  if (filters.sort_order) params.sort_order = filters.sort_order
  return params
}

export const clientService = {
  getAll: async (filters: ClientFilters): Promise<ClientPaginatedResponse> => {
    const { data } = await api.get('/clients/', {
      params: buildFilterParams(filters),
    })
    return data
  },

  getById: async (id: number): Promise<Client> => {
    const { data } = await api.get(`/clients/${id}`)
    return data
  },

  search: async (param: string, value: string, exactMatch = false): Promise<Client[]> => {
    const { data } = await api.get('/clients_by_param/', {
      params: { param, value, exact_match: exactMatch },
    })
    return data
  },

  create: async (client: ClientFormData & { id_company: number }): Promise<Client> => {
    const { data } = await api.post('/clients/', client)
    return data
  },

  update: async (id: number, client: Partial<ClientFormData>): Promise<Client> => {
    const { data } = await api.put(`/clients/${id}`, client)
    return data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/clients/${id}`)
  },

  getStatus: async (id: number): Promise<ClientStatus> => {
    const { data } = await api.get(`/status_client/${id}`)
    return data
  },

  exportAll: async (filters: Omit<ClientFilters, 'page' | 'limit'>): Promise<Blob> => {
    const params: Record<string, string | number | boolean> = {}
    if (filters.search) params.search = filters.search
    if (filters.commune) params.commune = filters.commune
    if (filters.gender) params.gender = filters.gender
    if (filters.last_known_category) params.last_known_category = filters.last_known_category
    if (filters.id_branch_office !== undefined) params.id_branch_office = filters.id_branch_office
    if (filters.created_from) params.created_from = filters.created_from
    if (filters.created_to) params.created_to = filters.created_to
    if (filters.sort_by) params.sort_by = filters.sort_by
    if (filters.sort_order) params.sort_order = filters.sort_order

    const response = await api.get('/export_clients/', {
      params,
      responseType: 'blob',
    })
    return response.data
  },
}
